import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar } from "@/components/admin/Avatar";

const EMPTY = { name: "", email: "", password: "", permissions: [] };

export function SubadminsPanel({ subs, perms, onChanged }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editSub, setEditSub] = useState(null);
  const [editPerms, setEditPerms] = useState([]);

  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  const toggleEditPerm = (p) => setEditPerms((ps) => ps.includes(p) ? ps.filter((x) => x !== p) : [...ps, p]);

  const openEdit = (s) => { setEditSub(s); setEditPerms(s.permissions || []); };
  const savePerms = async () => {
    try { await api.put(`/admin/subadmins/${editSub.id}`, { permissions: editPerms }); toast.success(t("dash.permsUpdated")); setEditSub(null); onChanged(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const createSub = async () => {
    try { await api.post("/admin/subadmins", form); toast.success(t("dash.subadminCreated")); setDialog(false); setForm(EMPTY); onChanged(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const deleteSub = async (id) => {
    try { await api.delete(`/admin/subadmins/${id}`); toast.success(t("dash.deleted")); onChanged(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex justify-end mb-4"><Button data-testid="add-subadmin-button" onClick={() => setDialog(true)} className="rounded-full bg-emeraude hover:bg-emeraude-dark font-bold"><Plus className="w-4 h-4 mr-1" /> {t("dash.newSubadmin")}</Button></div>
      {subs.length === 0 ? <div className="text-center py-12 bg-white rounded-2xl border border-border text-muted2 font-body">{t("dash.noSubadmins")}</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {subs.map((s) => (
            <div key={s.id} data-testid={`subadmin-${s.id}`} className="bg-white rounded-2xl shadow-card border border-border p-5">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3"><Avatar url={s.avatar_url} name={s.name} cls="w-11 h-11 text-lg" testid={`subadmin-avatar-${s.id}`} /><div><h3 className="font-heading font-bold text-ink">{s.name}</h3><p className="text-xs text-muted2">{s.email}</p></div></div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" data-testid={`edit-subadmin-${s.id}`} onClick={() => openEdit(s)} className="rounded-full h-8 text-canard-dark border-canard/30"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> {t("dash.editPerms")}</Button>
                  <Button size="sm" variant="outline" data-testid={`delete-subadmin-${s.id}`} onClick={() => deleteSub(s.id)} className="rounded-full h-8 text-destructive border-destructive/30"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-3">
                {(s.permissions || []).length === 0 ? <span className="text-xs text-muted2 font-body italic">{t("dash.noPerms")}</span> : (s.permissions || []).map((p) => <span key={p} className="text-xs bg-canard/10 text-canard-dark px-2 py-0.5 rounded-full font-semibold">{p}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{t("dash.createSubadmin")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="sub-name" placeholder={t("dash.subName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <Input data-testid="sub-email" type="email" placeholder={t("dash.subEmail")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <Input data-testid="sub-password" type="password" placeholder={t("dash.subPassword")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <div>
              <p className="text-xs font-bold uppercase text-muted2 mb-2">{t("dash.permissions")}</p>
              <div className="space-y-2">
                {perms.map((p) => (
                  <label key={p} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
                    <span className="text-sm font-body text-ink">{p}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button data-testid="sub-save-button" onClick={createSub} className="w-full h-11 rounded-full bg-canard hover:bg-canard-dark font-bold">{t("dash.create")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editSub} onOpenChange={(o) => !o && setEditSub(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-canard" /> {t("dash.editPermsTitle")} — {editSub?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {perms.map((p) => (
              <label key={p} className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={editPerms.includes(p)} onCheckedChange={() => toggleEditPerm(p)} data-testid={`edit-perm-${p}`} />
                <span className="text-sm font-body text-ink">{p}</span>
              </label>
            ))}
            <Button data-testid="edit-perms-save" onClick={savePerms} className="w-full h-11 rounded-full bg-emeraude hover:bg-emeraude-dark font-bold mt-2">{t("dash.savePerms")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
